/**
 * RFC 9457 problem documents, and turning one into something a form or a
 * toast can show.
 *
 * Every API failure arrives in this envelope — `mdm.core.problems` has a
 * boot assertion refusing to start an app whose document says otherwise —
 * so this is the only error shape the client has to understand.
 */

import type { components } from "@/lib/api/schema";

export type Problem = components["schemas"]["Problem"];
export type FieldError = components["schemas"]["FieldError"];

/**
 * A failed request, carrying the problem document.
 *
 * An Error subclass rather than a returned union: TanStack Query's
 * `onError` and error boundaries both key on throw, and a union would
 * mean every call site remembering to check. The document is on the
 * instance for the ones that care.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.status = problem.status;
    this.problem = problem;
  }

  /** The id to quote in a support ticket. Rendered by `ErrorState`. */
  get traceId(): string | undefined {
    return this.problem.traceId ?? undefined;
  }

  /** True for the one status that must never be retried automatically. */
  get isServiceUnavailable(): boolean {
    return this.status === 503;
  }
}

/**
 * Read a problem out of a response, whatever actually came back.
 *
 * A 502 from the load balancer, a proxy error page, a truncated body — all
 * real, none of them problem documents. Synthesising one here means the UI
 * has exactly one error shape to render instead of a special case for
 * "the error was itself malformed".
 */
export async function problemFrom(response: Response): Promise<Problem> {
  const fallback: Problem = {
    type: "about:blank",
    title: response.statusText || "Request failed",
    status: response.status,
  };

  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("json")) return fallback;

  try {
    const body: unknown = await response.json();
    if (typeof body !== "object" || body === null) return fallback;
    const problem = body as Partial<Problem>;
    return {
      ...problem,
      type: problem.type ?? fallback.type,
      title: problem.title ?? fallback.title,
      // Trust the response's own status over the body's: a proxy that
      // rewrote the status is the case where they disagree, and the one
      // the browser acted on is the true one.
      status: response.status,
    } as Problem;
  } catch {
    return fallback;
  }
}

/**
 * Map an RFC 6901 JSON Pointer from `problem.errors[]` onto a
 * react-hook-form field path.
 *
 * The server points into the *request* — `/body/display_name/hi`,
 * `/query/limit` — and RHF names fields by dotted path from the form root
 * — `display_name.hi`. Getting this wrong is silent: `setError` on a name
 * no field owns renders nothing at all, so the user sees a form that
 * refuses to submit and says nothing about why. Hence a named function
 * with its own tests rather than an inline expression.
 *
 * Returns null for a pointer that does not address the body, because
 * those belong in a toast, not on a field.
 */
export function fieldPathFor(pointer: string): string | null {
  if (!pointer.startsWith("/body")) return null;

  const segments = pointer
    .slice("/body".length)
    .split("/")
    .filter((segment) => segment.length > 0)
    // RFC 6901 escaping, and the order matters: ~1 before ~0, or a literal
    // `~1` in a field name decodes to `/` and addresses the wrong field.
    .map((segment) => segment.replaceAll("~1", "/").replaceAll("~0", "~"));

  if (segments.length === 0) return null;

  // Array indices become bracket notation — RHF reads `items.0.name` too,
  // but `items[0].name` is what it emits, and matching it keeps a
  // round-trip through `formState.errors` stable.
  return segments.reduce((path, segment) => {
    if (path === "") return segment;
    return /^\d+$/.test(segment) ? `${path}[${segment}]` : `${path}.${segment}`;
  }, "");
}

/** The field errors a form can display, already mapped to RHF paths. */
export function formErrorsFrom(problem: Problem): { path: string; message: string }[] {
  return (problem.errors ?? []).flatMap((error) => {
    const path = fieldPathFor(error.pointer);
    return path === null ? [] : [{ path, message: error.detail }];
  });
}
